// The barrier columns, and the order each screen puts them in.
//
// COLUMN ORDERS DIFFER MEANINGFULLY BETWEEN VIEWS and the source is the
// authority on them. What `#59` records of that authority is which columns each
// view leads with -- `Department` and `Interplant Transfers` lead with Plant,
// Area and Customer Name; `Barriers Dashboard` leads with Job Number and Date
// Of Escalation -- and the orders below honour those and are otherwise this
// file's. The exit criterion for the phase is screen-by-screen sign-off, which
// is where a column in the wrong place is caught.
//
// `Line` is not here and its absence is a decision rather than an omission: D7
// dropped `Barriers.Line` and the `Lines` table entirely, the column having been
// set on one row of 1,824 and read-only and hidden in the source.

import type { GridColumn } from '../grid/DataGrid';
import type { BarrierViewRow } from '../types';
import { actionCategory, actionOwner, enteredBy, formatDate, ownerNames } from '../format';
import type { Lookup } from '../data';

export interface BarrierColumnOptions {
  /** Inline edit, enabled on every barrier grid except `Plants`. */
  editable: boolean;
  /** Whether the signed-in person may change Hot. Only the people an Admin has
   *  flagged may, so for everyone else Hot is read-only even where the rest of
   *  the row edits inline. */
  canSetHot: boolean;
  /** id -> label for the foreign keys a cell can be re-pointed at. */
  lookups: Record<string, Lookup>;
}

function options(lookup: Lookup | undefined): { value: number; label: string }[] | undefined {
  if (!lookup) return undefined;
  return [...lookup.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Every column a barrier screen can show, keyed by the name a view names.
 *
 *  A foreign key appears once, showing the name the API's joins resolved and
 *  editing the id -- `Commodity` reads `commodity_name` and writes
 *  `commodity_id`. */
export function barrierColumns({
  editable,
  canSetHot,
  lookups,
}: BarrierColumnOptions): Record<string, GridColumn<BarrierViewRow>> {
  const reference = (
    key: string,
    display: string,
    label: string,
    className?: string,
    width?: number
  ): GridColumn<BarrierViewRow> => ({
    key: display,
    label,
    type: 'str',
    className,
    width,
    editable,
    editKey: key,
    editFrom: (row) => row[key],
    options: options(lookups[key]),
  });

  return {
    // The source styles these six unconditionally -- Plant names blue,
    // Products red, Commodities green, Departments teal, Action Category bold.
    // No condition is evaluated; they are column styling.
    plant: reference('site_id', 'site_name', 'Plant', 'style-plant', 140),
    area: reference('area_id', 'area_name', 'Area', undefined, 120),
    product: reference('product_id', 'product_type', 'Product', 'style-product', 140),
    department: reference('department_id', 'department_name', 'Owner Department', 'style-department', 160),
    commodity: reference('commodity_id', 'commodity_name', 'Commodity', 'style-commodity', 140),
    buyerStatus: reference('buyer_status_id', 'buyer_status_name', 'Buyer Status', undefined, 140),
    warehouseStatus: reference('warehouse_status_id', 'warehouse_status_name', 'Warehouse Status', undefined, 150),
    rootCause: reference('root_cause_id', 'root_cause_name', 'Root Cause', undefined, 150),
    makePartsPlant: reference('make_parts_site_id', 'make_parts_site_name', 'Make Parts Plant', 'style-plant', 150),

    jobNumber: {
      key: 'order_number',
      label: 'Job Number',
      type: 'str',
      width: 120,
    },
    customerName: { key: 'customer_name', label: 'Customer Name', type: 'str', width: 170 },

    barrierNumber: { key: 'barrier_number', label: 'Barrier', type: 'int', width: 80, align: 'right' },
    dateOfEscalation: { key: 'date_of_escalation', label: 'Date Of Escalation', type: 'date', width: 140, editable },
    originalCommitment: {
      key: 'original_commitment_date',
      label: 'Original Commitment Date',
      type: 'date',
      width: 160,
      editable,
    },
    currentCommitment: {
      key: 'current_commitment_date',
      label: 'Current Commitment Date',
      type: 'date',
      width: 160,
      editable,
    },
    actualClose: { key: 'actual_close_date', label: 'Actual Close Date', type: 'date', width: 140, editable },
    hot: { key: 'is_hot', label: 'Hot', type: 'bool', width: 60, editable: editable && canSetHot },
    problem: { key: 'problem_description', label: 'Problem Description', type: 'str', width: 320, editable },
    correctiveAction: { key: 'corrective_action', label: 'Corrective Action', type: 'str', width: 320, editable },
    partNumber: { key: 'part_number', label: 'Part Number', type: 'str', width: 140, editable },
    toNumber: { key: 'interplant_transfer_ref', label: 'TO Number', type: 'str', width: 140, editable },
    week1: { key: 'week_1_need_qty', label: 'Week 1 Need Qty', type: 'int', width: 120, align: 'right', editable },
    week2: { key: 'week_2_need_qty', label: 'Week 2 Need Qty', type: 'int', width: 120, align: 'right', editable },
    makeParts: { key: 'make_parts', label: 'Make Parts', type: 'bool', width: 90, editable },

    // Derived, and neither is a column in the database.
    actionCategory: {
      key: 'action_category',
      label: 'Action Category',
      type: 'str',
      // Wide enough for the longest composed label -- `IPT - Harris to
      // Philipp` and `Supplier Expedite` were both cut off with an ellipsis in
      // the group header, which is the one place the value has to be readable.
      width: 240,
      className: 'style-action',
      value: (row) => actionCategory(row),
    },
    owners: {
      key: 'owners',
      label: 'Owner',
      type: 'str',
      width: 200,
      // `Owner` is multi-valued -- 603 of the 1,543 owned barriers name more
      // than one person -- so this is every owner of the row, joined.
      value: (row) => ownerNames(row),
    },
    actionOwner: {
      key: 'action_owner',
      label: 'Action Owner',
      type: 'str',
      width: 170,
      // One person from the directory, which is what the filter menu filters on --
      // not `Owner` above. Chosen on the form with a search, so no grid edits it
      // inline. A name typed before it came from the directory says so.
      value: (row) => actionOwner(row),
    },
    enteredBy: {
      key: 'entered_by',
      label: 'Entered By',
      type: 'str',
      width: 170,
      // Set by the API from the token and never by a client, so no screen edits
      // it inline. `Legacy` on a migrated barrier -- see `enteredBy`.
      value: (row) => enteredBy(row),
    },
    escalatedFor: {
      key: 'escalated_for',
      label: 'Days Open',
      type: 'int',
      width: 90,
      align: 'right',
      value: (row) => daysOpen(row),
    },
  };
}

/** How long a barrier has been open, in days, which no column holds.
 *
 *  Counted to the close date where there is one and to today where there is
 *  not, so the archive's closed rows report what they took rather than how long
 *  ago they happened. */
export function daysOpen(row: BarrierViewRow): number | null {
  if (!row.date_of_escalation) return null;
  const start = Date.parse(`${String(row.date_of_escalation).slice(0, 10)}T00:00:00`);
  const end = row.actual_close_date
    ? Date.parse(`${String(row.actual_close_date).slice(0, 10)}T00:00:00`)
    : Date.now();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

/** The hot-row highlight: pink and bold, across the whole row.
 *
 *  The source spans this over 30 individual column rules, one per column. It is
 *  one class here, which is the same appearance and one rule to change. */
export function barrierRowClass(row: BarrierViewRow): string | undefined {
  return row.is_hot ? 'row-hot' : undefined;
}

export function barrierSummary(row: BarrierViewRow): string {
  const parts = [row.order_number, row.product_type, formatDate(row.date_of_escalation)].filter(Boolean);
  return `Barrier ${row.barrier_number}${parts.length ? ' — ' + parts.join(' · ') : ''}`;
}
